import {
  TranslatableEntry,
  TranslatableResourceTypeMapping,
} from 'features/Translations/TranslatableResource/types';
import {isGroupedKey} from 'features/Translations/TranslatableResource/utils';
import {TranslatableResourceType} from 'types';

const GROUP_ORDER: TranslatableResourceTypeMapping<number> = {
  [TranslatableResourceType.Metafield]: 0,
  [TranslatableResourceType.ProductOption]: 1,
  [TranslatableResourceType.ProductOptionValue]: 2,
};

const UNGROUPED_FIELDS_KEY = 'ungroupedFields';

interface SortingTranslatableEntriesObject {
  [key: string]: TranslatableEntry[];
  [UNGROUPED_FIELDS_KEY]: TranslatableEntry[];
}

function sortByGroupOrder(translatableResources: TranslatableEntry[]) {
  return translatableResources.sort((tr1, tr2) => {
    if (
      tr1.translatableResourceType ===
        TranslatableResourceType.ProductOptionValue &&
      tr2.translatableResourceType ===
        TranslatableResourceType.ProductOptionValue
    ) {
      return tr1.key > tr2.key ? 1 : -1;
    }
    return (
      (GROUP_ORDER[tr1.translatableResourceType] ?? 0) -
      (GROUP_ORDER[tr2.translatableResourceType] ?? 0)
    );
  });
}

const buildSortingResourceField = (
  acc: SortingTranslatableEntriesObject,
  index: number | string,
  field: TranslatableEntry,
) => ({...acc, [index]: [...(acc[index] ?? []), field]});

export function sortEntriesForDisplay(
  translatableTranslatableEntries: TranslatableEntry[],
) {
  const {ungroupedFields, ...groupedProductOptionVariantFields} =
    translatableTranslatableEntries.reduce<SortingTranslatableEntriesObject>(
      (acc, field) => {
        const {
          translatableResourceType,
          key,
          display,
          defaultContent,
          metafieldDetails,
        } = field;

        if (
          translatableResourceType === TranslatableResourceType.ProductOption
        ) {
          return buildSortingResourceField(
            acc,
            display.position ?? UNGROUPED_FIELDS_KEY,
            field,
          );
        }
        if (
          translatableResourceType ===
            TranslatableResourceType.ProductOptionValue &&
          isGroupedKey(translatableResourceType, key)
        ) {
          if (!defaultContent.value) {
            return acc;
          }

          const optionIndex = display.position ?? UNGROUPED_FIELDS_KEY;

          const modifiedField = {
            ...field,
            display: {
              ...field.display,
              title:
                translatableTranslatableEntries.find(
                  (resourceField) =>
                    resourceField.translatableResourceType ===
                      TranslatableResourceType.ProductOption &&
                    resourceField.key === 'name' &&
                    resourceField.display.position === optionIndex,
                )?.defaultContent.value ?? undefined,
            },
          };

          return buildSortingResourceField(acc, optionIndex, modifiedField);
        }

        if (
          translatableResourceType === TranslatableResourceType.Metafield &&
          metafieldDetails &&
          isGroupedKey(
            translatableResourceType,
            key,
            metafieldDetails?.ownerType,
          )
        ) {
          if (!defaultContent.value) {
            return acc;
          }

          const modifiedField = {
            ...field,
            display: {
              ...field.display,
              title: display.title,
            },
          };

          return buildSortingResourceField(
            acc,
            `${metafieldDetails.namespace}-${metafieldDetails.key}`,
            modifiedField,
          );
        }

        return buildSortingResourceField(acc, UNGROUPED_FIELDS_KEY, field);
      },
      {[UNGROUPED_FIELDS_KEY]: []},
    );

  return [
    ...sortByGroupOrder(ungroupedFields),
    ...Object.values(groupedProductOptionVariantFields).flatMap((entry) =>
      sortByGroupOrder(entry),
    ),
  ];
}
